{
    "$id": "https://augmented.team/schemas/charter.frontmatter.v1.json",
    "$schema": "https://json-schema.org/draft/2020-12/schema",
    "title": "CHARTER.md Frontmatter v1",
    "type": "object",
    "required": [
        "agent_id",
        "code_name",
        "display_name",
        "version",
        "environment",
        "owner",
        "risk_tier",
        "logging_mode",
        "created",
        "last_updated"
    ],
    "properties": {
        "agent_id": {
            "type": "string",
            "minLength": 3,
            "maxLength": 128
        },
        "code_name": {
            "type": "string",
            "pattern": "^[a-z0-9]+(-[a-z0-9]+)*$"
        },
        "display_name": {
            "type": "string",
            "minLength": 2,
            "maxLength": 128
        },
        "version": {
            "type": "string",
            "pattern": "^[0-9]+\\.[0-9]+(\\.[0-9]+)?$"
        },
        "environment": {
            "type": "string",
            "enum": [
                "dev",
                "stage",
                "prod"
            ]
        },
        "owner": {
            "type": "object",
            "required": [
                "id",
                "name"
            ],
            "properties": {
                "id": {
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 128
                },
                "name": {
                    "type": "string",
                    "minLength": 1,
                    "maxLength": 128
                },
                "email": {
                    "type": "string",
                    "format": "email"
                }
            },
            "additionalProperties": false
        },
        "risk_tier": {
            "type": "string",
            "enum": [
                "Low",
                "Medium",
                "High"
            ]
        },
        "logging_mode": {
            "type": "string",
            "enum": [
                "hash-only",
                "redacted",
                "full-local"
            ]
        },
        "budget": {
            "type": "object",
            "required": [
                "type",
                "limit",
                "window"
            ],
            "properties": {
                "type": {
                    "type": "string",
                    "enum": [
                        "tokens",
                        "dollars",
                        "both"
                    ]
                },
                "limit": {
                    "type": "number",
                    "exclusiveMinimum": 0
                },
                "limit_tokens": {
                    "type": "integer",
                    "minimum": 1
                },
                "limit_dollars": {
                    "type": "number",
                    "exclusiveMinimum": 0
                },
                "window": {
                    "type": "string",
                    "enum": [
                        "daily",
                        "weekly",
                        "monthly"
                    ]
                },
                "enforcement": {
                    "type": "string",
                    "enum": [
                        "alert",
                        "throttle",
                        "block",
                        "degrade"
                    ]
                }
            },
            "allOf": [
                {
                    "if": {
                        "properties": {
                            "type": {
                                "const": "tokens"
                            }
                        }
                    },
                    "then": {
                        "required": [
                            "limit_tokens"
                        ]
                    }
                },
                {
                    "if": {
                        "properties": {
                            "type": {
                                "const": "dollars"
                            }
                        }
                    },
                    "then": {
                        "required": [
                            "limit_dollars"
                        ]
                    }
                },
                {
                    "if": {
                        "properties": {
                            "type": {
                                "const": "both"
                            }
                        }
                    },
                    "then": {
                        "required": [
                            "limit_tokens",
                            "limit_dollars"
                        ]
                    }
                }
            ],
            "additionalProperties": false
        },
        "limits": {
            "type": "object",
            "required": [
                "max_tokens_per_request",
                "max_tokens_per_run"
            ],
            "properties": {
                "max_tokens_per_request": {
                    "type": "integer",
                    "minimum": 1,
                    "maximum": 200000
                },
                "max_tokens_per_run": {
                    "type": "integer",
                    "minimum": 1,
                    "maximum": 200000
                }
            },
            "additionalProperties": false
        },
        "channels": {
            "type": "object",
            "required": [
                "policy"
            ],
            "properties": {
                "policy": {
                    "type": "string",
                    "enum": [
                        "allowlist",
                        "denylist"
                    ]
                },
                "allowed": {
                    "type": "array",
                    "items": {
                        "type": "string",
                        "enum": [
                            "slack",
                            "msteams",
                            "telegram",
                            "whatsapp",
                            "signal",
                            "discord",
                            "irc",
                            "matrix",
                            "mattermost",
                            "imessage",
                            "google-chat",
                            "nostr",
                            "line",
                            "feishu",
                            "nextcloud-talk",
                            "zalo",
                            "tlon",
                            "bluebubbles",
                            "beam",
                            "direct-chat",
                            "grok-voice"
                        ]
                    },
                    "uniqueItems": true
                },
                "denied": {
                    "type": "array",
                    "items": {
                        "type": "string",
                        "enum": [
                            "slack",
                            "msteams",
                            "telegram",
                            "whatsapp",
                            "signal",
                            "discord",
                            "irc",
                            "matrix",
                            "mattermost",
                            "imessage",
                            "google-chat",
                            "nostr",
                            "line",
                            "feishu",
                            "nextcloud-talk",
                            "zalo",
                            "tlon",
                            "bluebubbles",
                            "beam",
                            "direct-chat",
                            "grok-voice"
                        ]
                    },
                    "uniqueItems": true
                },
                "require_approval_to_change": {
                    "type": "boolean",
                    "default": true
                },
                "sender_policy": {
                    "type": "string",
                    "enum": ["all", "agents_only", "team_only", "team_agents_only", "manager_only"],
                    "description": "Restricts which senders this agent processes. 'all' (default): anyone. 'agents_only': only Augmented-labelled agents. 'team_only' (ENG-5871): humans on the same team (resolved via team_members ⋈ organization_people on user_id, NOT NULL) OR same-team Augmented agents. 'team_agents_only': only same-team Augmented agents (humans dropped). 'manager_only' (ENG-5842, narrowed by ENG-10685): ONLY the agent's reports_to_person — one permitted counterparty, with no agent carve-out. Other humans AND other Augmented agents are dropped; use 'team_agents_only' if peer coordination is wanted. Enforced via message metadata labels (Slack/Teams) and principal-id env vars resolved at provision time."
                }
            },
            "additionalProperties": false
        },
        "multi_agent": {
            "type": "object",
            "description": "ENG-4465 + ENG-4970: per-agent peer-collaboration registry. Telegram + Slack.",
            "properties": {
                "telegram_peers": {
                    "type": "array",
                    "description": "Agents this agent may collaborate with via Telegram Bot-to-Bot Mode. bot_id is the immutable from.id of the peer's Telegram bot; code_name is for humans.",
                    "items": {
                        "type": "object",
                        "required": [
                            "code_name",
                            "bot_id"
                        ],
                        "properties": {
                            "code_name": {
                                "type": "string",
                                "pattern": "^[a-z0-9]+(-[a-z0-9]+)*$"
                            },
                            "bot_id": {
                                "type": "integer",
                                "exclusiveMinimum": 0
                            },
                            "cross_team_grant_id": {
                                "type": "string",
                                "format": "uuid",
                                "description": "ENG-4938 / ENG-4929 §5: optional cross_team_peer_grants.grant_id authorising messages to a peer on a different team. Omit for same-team peers."
                            }
                        },
                        "additionalProperties": false
                    },
                    "uniqueItems": true
                },
                "slack_peers": {
                    "type": "array",
                    "description": "ENG-4970 / ENG-4974: agents this agent may collaborate with via Slack. bot_user_id is the immutable Slack `U…` identity of the peer's bot user; code_name is for humans. Mirrors telegram_peers but keyed on Slack user_id since Slack's bot identity is a user_id, not an integer bot_id.",
                    "items": {
                        "type": "object",
                        "required": [
                            "code_name",
                            "bot_user_id"
                        ],
                        "properties": {
                            "code_name": {
                                "type": "string",
                                "pattern": "^[a-z0-9]+(-[a-z0-9]+)*$"
                            },
                            "bot_user_id": {
                                "type": "string",
                                "pattern": "^U[A-Z0-9]{6,}$",
                                "description": "The peer Slack bot's user_id (the `U…` identifier returned by auth.test as `user_id`). Immutable per bot installation."
                            },
                            "cross_team_grant_id": {
                                "type": "string",
                                "format": "uuid",
                                "description": "ENG-4970 / ENG-4972: optional cross_team_peer_grants.grant_id authorising messages to a peer on a different team. Omit for same-team peers."
                            }
                        },
                        "additionalProperties": false
                    },
                    "uniqueItems": true
                }
            },
            "additionalProperties": false
        },
        "tools": {
            "type": "object",
            "description": "ENG-6707: agent-driven skill authoring is governed by the SkillSpector scanner gate; tools.skills carries only the shared-scope kill switch.",
            "properties": {
                "skills": {
                    "type": "object",
                    "properties": {
                        "shared_authoring": {
                            "type": "boolean",
                            "default": true,
                            "description": "ENG-6707 kill switch for agent-driven shared-scope (team/organization) skill authoring. Default true (open): shared skills auto-publish on a clean SkillSpector scan, else land as drafts for operator review (fail-closed). Set false to revoke shared-scope authoring for a compromised agent; agent-scope authoring stays available."
                        },
                        "write_team": {
                            "type": "boolean",
                            "default": false,
                            "deprecated": true,
                            "description": "DEPRECATED (ENG-6707): ignored. Superseded by the scanner gate + shared_authoring kill switch. Retained so charters written before the migration still validate."
                        },
                        "write_organization": {
                            "type": "boolean",
                            "default": false,
                            "deprecated": true,
                            "description": "DEPRECATED (ENG-6707): ignored. Org-scope authoring is governed by the same scanner gate + shared_authoring kill switch as team scope. Retained so charters written before the migration still validate."
                        },
                        "publish": {
                            "type": "boolean",
                            "default": false,
                            "deprecated": true,
                            "description": "DEPRECATED (ENG-6707): ignored. Auto-publish is now driven by a clean SkillSpector scan, not this flag. Retained for back-compat."
                        }
                    },
                    "additionalProperties": false
                }
            },
            "additionalProperties": false
        },
        "created": {
            "type": "string",
            "format": "date"
        },
        "last_updated": {
            "type": "string",
            "format": "date"
        }
    },
    "additionalProperties": false
}
